import { useState } from "react";
import {
  BookOpen, Copy, Check, ExternalLink, ArrowRight, Info, Megaphone,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Iter 108r — Runbook that walks the operator through wiring
// structured.ai (the vendor behind Conduit's Marketing app) to
// Conduit's OIDC identity provider. Mirrors the shape of
// TalentLMSRunbook so operators recognise the pattern.
//
// Structured.ai's SSO admin UI labels may shift over time — the
// generic OIDC RP terminology used here (Discovery URL, redirect URI,
// client authentication method, claim mapping) maps 1:1 to any
// standards-compliant OIDC configuration surface.

function Copyable({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Clipboard blocked"); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      {label && <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>}
      <div className="mt-0.5 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 font-mono text-xs text-slate-800" data-testid={`${testid}-value`}>
          {value}
        </code>
        <Button size="sm" variant="outline" onClick={doCopy} data-testid={`${testid}-copy`} className="h-7">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="grid grid-cols-[32px_1fr] gap-3">
      <div className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 font-heading text-xs font-bold text-primary">
        {n}
      </div>
      <div className="min-w-0">
        <div className="font-heading text-sm font-semibold text-slate-800">{title}</div>
        <div className="mt-1 space-y-2 text-sm text-slate-600">{children}</div>
      </div>
    </div>
  );
}

export function StructuredRunbook({ open, onOpenChange }) {
  const origin = window.location.origin;
  const discovery = `${origin}/api/oauth/.well-known/openid-configuration`;
  const authorize = `${origin}/api/oauth/authorize`;
  const tokenEp = `${origin}/api/oauth/token`;
  const userinfoEp = `${origin}/api/oauth/userinfo`;
  const jwksEp = `${origin}/api/oauth/jwks.json`;
  // Placeholder — replace with the exact URLs structured.ai issues to
  // your tenant once the SSO surface is enabled on their side.
  const structuredRedirectUri = "https://<your-tenant>.structured.ai/oauth/callback";
  const structuredInitUrl     = "https://<your-tenant>.structured.ai/sso/oidc/start";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="structured-runbook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" /> structured.ai &rarr; Conduit OIDC setup
          </DialogTitle>
          <DialogDescription>
            One-time configuration to make structured.ai (the vendor behind Conduit&rsquo;s <b>Marketing</b> app) delegate authentication to Conduit. Once done, any Conduit user who launches Marketing lands on their structured.ai dashboard as themselves &mdash; no shared cookies, no manual login.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-slate-600" data-testid="structured-runbook-prereqs">
          <div className="mb-1 flex items-center gap-1.5 font-semibold text-primary">
            <Info className="h-3.5 w-3.5" /> Before you begin
          </div>
          <ul className="list-disc space-y-0.5 pl-5">
            <li>You need a structured.ai plan that includes <b>Enterprise SSO / OIDC</b> (contact their success team if unsure).</li>
            <li>You need <b>admin/owner</b> access in both Conduit and structured.ai.</li>
            <li>Have this Conduit tab and a structured.ai admin tab open side-by-side.</li>
            <li>The Marketing app already exists in Conduit (slug <code className="font-mono">marketing</code>). This guide wires the OIDC linkage.</li>
          </ul>
        </div>

        <div className="mt-5 space-y-5">
          <Step n={1} title="Register structured.ai as an OAuth2 client in Conduit">
            <p>Click <b>New client</b> in the panel behind this dialog. Fill in:</p>
            <ul className="ml-4 list-disc space-y-1 text-xs">
              <li><b>Name</b>: <code className="rounded bg-slate-100 px-1 font-mono">structured.ai (Marketing)</code></li>
              <li><b>Redirect URIs</b> &mdash; grab the exact value from structured.ai&rsquo;s SSO settings panel. It typically looks like:</li>
            </ul>
            <Copyable value={structuredRedirectUri} testid="structured-runbook-redirect" />
            <ul className="ml-4 list-disc space-y-1 text-xs">
              <li><b>Allowed scopes</b>: <code className="mx-0.5 rounded bg-slate-100 px-1 font-mono">openid profile email groups partner_org</code></li>
              <li><b>Trusted first-party client</b>: <span className="rounded bg-emerald-100 px-1 font-semibold text-emerald-800">ON</span> &mdash; skips the consent screen for internal users.</li>
              <li><b>Enforce JWKS</b>: leave <b>OFF</b> unless structured.ai&rsquo;s docs explicitly require RS256 signatures. Conduit signs id_tokens with HS256 using the client secret in this mode &mdash; broadly compatible with OIDC RPs and matches how Training is wired.</li>
              <li><b>Enforce PKCE</b>: leave <b>OFF</b> unless structured.ai forces PKCE for confidential clients.</li>
            </ul>
            <p className="text-xs text-slate-500">Click <b>Create client</b>. Copy the <b>Client ID</b> and <b>Client Secret</b> shown in the reveal dialog &mdash; the secret is only visible once.</p>
          </Step>

          <Step n={2} title="Open structured.ai's SSO / OIDC settings">
            <p>In structured.ai, go to <b>Settings &rarr; Security &rarr; Single Sign-On</b> (or <b>Admin &rarr; Authentication</b>, depending on tenant). Choose <b>OpenID Connect</b> as the provider type.</p>
            <p className="text-xs text-slate-500">If structured.ai offers only SAML today, escalate to their support to enable OIDC for your tenant &mdash; the endpoints below only work with OIDC.</p>
          </Step>

          <Step n={3} title="Paste Conduit's OIDC endpoints into structured.ai">
            <p>If structured.ai supports OIDC <b>Discovery</b> (most modern platforms do), paste this single URL &mdash; every endpoint below will auto-populate:</p>
            <Copyable label="OIDC Discovery URL" value={discovery} testid="structured-runbook-discovery" />
            <p className="text-xs text-slate-500">If structured.ai requires each endpoint individually, use these:</p>
            <Copyable label="Issuer" value={`${origin}/api/oauth`} testid="structured-runbook-issuer" />
            <Copyable label="Authorization endpoint" value={authorize} testid="structured-runbook-authz" />
            <Copyable label="Token endpoint" value={tokenEp} testid="structured-runbook-token" />
            <Copyable label="UserInfo endpoint" value={userinfoEp} testid="structured-runbook-userinfo" />
            <Copyable label="JWKS URI" value={jwksEp} testid="structured-runbook-jwks" />
          </Step>

          <Step n={4} title="Paste the client credentials">
            <p>Paste the <b>Client ID</b> and <b>Client Secret</b> you copied in Step 1 into structured.ai&rsquo;s OIDC form. For &ldquo;Client Authentication Method&rdquo;, choose <b>client_secret_basic</b> or <b>client_secret_post</b> &mdash; either works. For &ldquo;Scope&rdquo;, enter <code className="mx-0.5 rounded bg-slate-100 px-1 font-mono">openid profile email groups partner_org</code>. For &ldquo;Signing algorithm&rdquo;, select <b>HS256</b> (matches the client&rsquo;s <b>Enforce JWKS: OFF</b> setting from Step 1).</p>
          </Step>

          <Step n={5} title="Map Conduit claims to structured.ai user fields">
            <p>Under &ldquo;User attribute mapping&rdquo; (or &ldquo;JIT provisioning&rdquo;) in structured.ai, wire Conduit&rsquo;s userinfo claims to structured.ai user fields:</p>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">structured.ai field</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Conduit claim</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  <tr><td className="px-2 py-1.5 font-mono">username / login</td><td className="px-2 py-1.5 font-mono">preferred_username</td><td className="px-2 py-1.5">Falls back to email if unset</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">email</td>            <td className="px-2 py-1.5 font-mono">email</td>              <td className="px-2 py-1.5">Used as the JIT provisioning key</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">first_name</td>       <td className="px-2 py-1.5 font-mono">given_name</td>         <td className="px-2 py-1.5">&mdash;</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">last_name</td>        <td className="px-2 py-1.5 font-mono">family_name</td>        <td className="px-2 py-1.5">&mdash;</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">avatar_url</td>       <td className="px-2 py-1.5 font-mono">picture</td>            <td className="px-2 py-1.5">Optional</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">org / workspace</td>  <td className="px-2 py-1.5 font-mono">partner_org</td>        <td className="px-2 py-1.5">Partner or customer org name from Conduit</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">groups / role</td>    <td className="px-2 py-1.5 font-mono">groups</td>             <td className="px-2 py-1.5">Team name from Conduit (Executive, Sales, Marketing, etc.)</td></tr>
                  <tr><td className="px-2 py-1.5 font-mono">tier / segment</td>   <td className="px-2 py-1.5 font-mono">partner_type</td>       <td className="px-2 py-1.5">Reseller / Agent / Customer</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500">If structured.ai wants the <code className="font-mono">groups</code> claim as a comma-separated string instead of a JSON array, apply the transformer on their mapping side &mdash; Conduit&rsquo;s <code className="font-mono">/userinfo</code> endpoint returns an array today.</p>
          </Step>

          <Step n={6} title="Turn on OIDC and grab the init URL">
            <p>Save the structured.ai OIDC form and toggle SSO <b>ON</b>. Structured.ai will show you the <b>OIDC sign-in URL</b> (label may vary: &ldquo;SP-initiated login URL&rdquo;, &ldquo;SSO start URL&rdquo;):</p>
            <Copyable label="structured.ai OIDC init URL (example)" value={structuredInitUrl} testid="structured-runbook-ssourl" />
            <p className="text-xs text-slate-500">Copy the exact URL structured.ai gives you &mdash; that&rsquo;s what Conduit&rsquo;s Marketing app will deep-link to.</p>
          </Step>

          <Step n={7} title="Point Conduit's Marketing app at the OIDC URL">
            <p>Open <b>Settings &rarr; Admin &rarr; Apps</b>, click the &hellip; menu on the <b>Marketing</b> card and choose <b>Edit</b>. Set:</p>
            <ul className="list-disc space-y-1 pl-5 text-xs">
              <li><b>URL</b> &mdash; paste the structured.ai OIDC init URL from Step 6.</li>
              <li><b>Embed allowed</b> &mdash; keep <b>ON</b>.</li>
              <li><b>OAuth2 client</b> &mdash; select the <code className="font-mono">structured.ai (Marketing)</code> client you registered in Step 1.</li>
            </ul>
            <p className="text-xs text-slate-500">Save. The iframe will now navigate to structured.ai&rsquo;s OIDC init URL &rarr; structured.ai 302s to Conduit&rsquo;s <code className="font-mono">/api/oauth/authorize</code> &rarr; Conduit issues a code using the current session cookie &rarr; structured.ai&rsquo;s callback swaps it for tokens and logs the user in.</p>
            <p className="text-xs text-slate-500">If structured.ai&rsquo;s dashboard sets <code className="font-mono">X-Frame-Options: SAMEORIGIN</code> on some pages, the always-visible &ldquo;Open in new tab&rdquo; button in the Conduit app header acts as the fallback &mdash; users still get to Marketing, just outside the iframe.</p>
            <p className="text-xs text-slate-500"><b>Logout endpoint</b> on the OIDC client: structured.ai has no <code className="font-mono">/logout</code> route, but its <code className="font-mono">/session-expired</code> page clears the <code className="font-mono">sai-api_token</code> cookie and SSO storage on load. Set it to <code className="font-mono">https://unitedprm.com/session-expired</code> so a stale session (yours, while viewing as someone else) is wiped before each launch.</p>
          </Step>

          <Step n={8} title="Verify end-to-end">
            <ul className="list-disc space-y-1 pl-5 text-xs">
              <li>Log in to Conduit as a <b>super-admin</b>. Click <b>Marketing</b>. You should land on structured.ai as the same user.</li>
              <li>Log out. Log in as a <b>partner or customer</b> user. Click Marketing. You should land as that user, mapped to the correct org / group.</li>
              <li>Log out and log in as a different user. Click Marketing. You should land as the <em>new</em> user &mdash; not the previous one.</li>
              <li>Watch every hop live in <b>OAuth Tracer</b> (Activity page &rarr; OAuth Tracer section, super admins only). You should see: <code className="font-mono">app_launch &rarr; authorize granted &rarr; token_issued alg=RS256 &rarr; userinfo served &rarr; &loz; Browser iframe onLoad fired</code>.</li>
              <li>In structured.ai, verify the user&rsquo;s first name / last name / avatar / org / groups all match Conduit.</li>
            </ul>
          </Step>

          <Step n={9} title="Emergency controls (per-client)">
            <p>If structured.ai or another OIDC peer is compromised:</p>
            <ul className="list-disc space-y-1 pl-5 text-xs">
              <li><b>Rotate secret</b> &mdash; keeps the client_id, changes the secret. Peer must be reconfigured with the new secret.</li>
              <li><b>Sign out all users</b> &mdash; invalidates every access + refresh token so every user re-authenticates on next request.</li>
              <li><b>Disable</b> &mdash; instantly blocks all new authorize + token requests. Toggle back on when safe.</li>
              <li><b>Delete</b> &mdash; permanent. Removes the client and invalidates every outstanding token.</li>
              <li><b>Rotate signing key</b> (panel header) &mdash; mints a fresh RSA keypair. Old key stays in JWKS for the id_token TTL window so cached verifiers keep working; retires automatically.</li>
            </ul>
          </Step>
        </div>

        <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline" onClick={() => window.open(discovery, "_blank")} data-testid="structured-runbook-open-discovery">
            <ExternalLink className="h-3.5 w-3.5" /> Open discovery URL
          </Button>
          <Button variant="outline" onClick={() => window.open("https://structured.ai/", "_blank")} data-testid="structured-runbook-open-vendor">
            <ExternalLink className="h-3.5 w-3.5" /> Open structured.ai
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="structured-runbook-close">
            Done <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
